import { Component, Vue, Watch } from 'vue-property-decorator';
import { SettingsReport } from '@/models/settingsReport/settingsReport';
import { SettingsReportStore } from '@/store/store.settingsReport';
import { SynchronizationService, AppSettingsService, OnlineService, DataSetService, ConfigurationService, QuickEditService } from '@/services/index';
import { EventStore } from '@/store/store.event';
import { DexieDataSetEvent } from '@/models/dexie/UnsyncedDexieDataSet';
import { AppSettingsStore } from '@/store/store.appSettings';
import { AppSettings } from '@/models/settings/AppSettings';
import { AuthentificationService } from '@/services/index';
import { UserProfileStore } from '@/store/store.userProfile';
import { LoggerService } from '@/utils';
import { AuthStore } from '@/store/store.auth';
import { OnlineStatusEnums } from '@/enums/OnlineStatusEnums';
import { ExcelExportService } from '@/services';
import { QuickGuide } from '@/models/quickGuide/QuickGuide';
import { QuickGuideStore } from '@/store/store.quickGuide';
import { Helpers } from '@/helpers/Helpers';
import { UserWhitelistEnums } from '@/enums/UserWhitelistEnums';
import { CreationMenuItem } from '@/models/general/CreationMenuItem';
import { CreationMenuTypeEnums } from '@/enums/CreationMenuTypeEnums';
import { QuickEditBaseModel } from '@/models/quickEdit/QuickEditBase';
import { DexieFilter } from '@/models/dexie/DexieFilter';
import { FilterNewService } from '@/services/index';

import HeaderLogo from '@/components/general/headerlogo/HeaderLogo.vue';
import TitleButton from '@/components/general/titlebutton/TitleButton.vue';
import FilterCard from '@/components/general/filtercard/FilterCard.vue';
import UnsyncedList from '@/components/general/unsyncedlist/UnsyncedList.vue';
import DebugMenu from '@/components/debugmenu/DebugMenu.vue';
import UserProfileMenu from '@/components/general/userprofilemenu/UserProfileMenu.vue';
import UnitsMenu from '@/components/general/unitsmenu/UnitsMenu.vue';

import build from '@/assets/build-number.json';

@Component({
    components: {
        HeaderLogo,
        TitleButton,
        FilterCard,
        UnsyncedList,
        DebugMenu,
        UserProfileMenu,
        UnitsMenu,
    },
})
export default class DashboardComponent extends Vue {
    public menuCreation: boolean = false;
    public menuDownload: boolean = false;
    public menuQuickGuide: boolean = false;
    public userGreeting: string = '';
    public showUnsynced: boolean = false;
    public showUnitsMenu: boolean = false;
    public disableUnsyncedList: boolean = true;

    public settingsReports: SettingsReport[] = [];
    public creationMenuItems: CreationMenuItem[] = [];
    public isLoggedIn: boolean = false;
    public isWhitelisted: UserWhitelistEnums = UserWhitelistEnums.UNKNOWN;
    public userWhitelistEnums = UserWhitelistEnums;
    public offlineMode: boolean = false;
    public quickGuides: QuickGuide[] = [];

    public filters: DexieFilter[] = [];

    public buildVersion = process.env.VUE_APP_TITLE;
    public buildNumber = build !== null ? build.buildNumber : '0.0.0.0';

    public loadingOverlay: boolean = false;

    public debugEnabled: boolean = process.env.VUE_APP_DEVELOPE === 'true' ? true : false;
    public debugMenu: boolean = false;

    private appSettings?: AppSettings;

    public beforeMount() {
        OnlineService.OnOnlineStausChanged.on((inStatus) => {
            LoggerService.LogDebug(`OnOnlineStausChanged: `, inStatus);
            if (inStatus && inStatus.old !== inStatus.new) {
                SettingsReportStore.FetchSettingsReports().catch((error) => {
                    LoggerService.LogError(`settingsReports: `, error);
                });

                QuickGuideStore.FetchQuickGuide().catch((error) => {
                    LoggerService.LogError(`quickGuide: `, error);
                });
            }
        });

        AppSettingsService.OnAppSettingsLoaded.on(async (inAppSettings) => {
            await this.onAppSettingsChanged(inAppSettings);
            await this.checkSession();
        });
    }
    // tslint:disable-next-line: no-empty
    public async mounted() {
        LoggerService.LogDebug(`DashboardComponent mounted`);

        this.settingsReports = this.getSettingsReports;
        this.quickGuides = this.getQuickGuide;
        this.isLoggedIn = this.getIsLoggedIn;
        // this.isWhitelisted = this.getIsWhitelisted;
        this.setCreationMenuItems();
        QuickEditService.OnQuickEditItemsChanged.on((data) => {
            this.setCreationMenuItems();
        });

        // stuff for loading filters
        FilterNewService.OnFiltersUpdated.on(this.onFiltersUpdated);
        this.onFiltersUpdated();

        if (AppSettingsService.GetAppSettingsLoaded === true) {
            await this.checkSession();
        }

        /**
         * There are some special navigation cases we can handle here.
         * For example when saving in quick view we should open the table,
         * but to not break navigation we go first back to dashboard and
         * then open taable view.
         */
        if (AppSettingsService.GetNavigateTo && AppSettingsService.GetNavigateTo.length > 0) {
            const to = AppSettingsService.GetNavigateTo;
            AppSettingsService.SetNavigateTo = undefined;
            this.$nextTick(() => {
                this.$router.push({ name: to });
            });
        }
    }

    public beforeDestroy() {
        LoggerService.LogDebug(`DashboardComponent beforeDestroy`);

        OnlineService.OnOnlineStausChanged.off(() => null);
        AppSettingsService.OnAppSettingsLoaded.off(() => null);
        QuickEditService.OnQuickEditItemsChanged.off(() => null);
        FilterNewService.OnFiltersUpdated.off(this.onFiltersUpdated);
    }

    get getSettingsReports() {
        return SettingsReportStore.settingsReports;
    }

    get getUnsyncedDataSets() {
        return EventStore.unsyncedEvents;
    }

    get getAppStarted() {
        return AppSettingsStore.appStarted;
    }

    get getIsLoggedIn() {
        return AuthStore.isLoggedIn;
    }

    get getIsWhitelisted() {
        return AuthStore.isWhitelisted;
    }

    get getQuickGuide() {
        return QuickGuideStore.GetQuickGuide;
    }

    @Watch('getUnsyncedDataSets', { deep: true, immediate: true })
    public onGetUnsyncedDataSetsChanged(newValue: DexieDataSetEvent[]) {
        // LoggerService.LogDebug(`onGetUnsyncedDataSetsChanged: ${newValue}`);
        this.disableUnsyncedList = newValue && newValue.length <= 0;
    }

    @Watch('getSettingsReports', { deep: true, immediate: true })
    public onSettingsReportsChanged(newValue: SettingsReport[]) {
        // LoggerService.LogDebug(`onSettingsReportsChanged: ${newValue}`);
        this.settingsReports = newValue;
    }

    @Watch('getQuickGuide', { deep: true, immediate: true })
    public onQuickGuideChanged(newValue: QuickGuide[]) {
        this.quickGuides = newValue;
    }

    @Watch('getIsLoggedIn', { deep: false, immediate: false })
    public async onIsLoggedInChanged(newValue: boolean) {
        LoggerService.LogDebug(`onIsLoggedInChanged: ${newValue}`);
        this.isLoggedIn = newValue;
        await this.checkUserStatus();
    }

    @Watch('getIsWhitelisted', { deep: false, immediate: true })
    public async onIsWhitelisted(newValue: UserWhitelistEnums) {
        LoggerService.LogDebug(`onIsWhitelisted: ${newValue}`);
        this.isWhitelisted = newValue;
        await this.checkUserStatus();
    }

    public async onAppSettingsChanged(newValue?: AppSettings) {
        if (newValue !== undefined) {
            this.appSettings = newValue;
            await this.checkUserStatus();
            if (AppSettingsStore.GetDirectOpenId != null) {
                AppSettingsService.SetDirectOpenId(AppSettingsStore.GetDirectOpenId);
                AppSettingsStore.SetDirectOpenId(undefined);
            }
            // if (this.getAppStarted === false && this.isLoggedIn === true) {
            //     await this.loadConfigData();
            // }
        } else {
            LoggerService.LogError(`Error on onAppSettingsChanged, appsettings undefined`);
        }
    }

    public onUnsyncedList() {
        this.showUnsynced = true;
    }

    public onOpenFilterNew(event: Event) {
        this.$router.push('filterNew');
    }

    public onAllData(event: Event) {
        this.$router.push('table');
    }

    public onCreation(event: Event) {
        this.menuCreation = !this.menuCreation;
    }

    public onCreate(item: CreationMenuItem) {
        switch (item.type) {
            case CreationMenuTypeEnums.WIZARD:
                this.$router.push({ name: 'quickeditWizard' });
                break;
            case CreationMenuTypeEnums.STANDARD:
                this.$router.push({ name: 'edit' });
                break;
            case CreationMenuTypeEnums.MYMILL_WIZARD:
                this.$router.push({ name: 'myMillWizard' });
                break;
            case CreationMenuTypeEnums.MOBILE:
                this.$router.push({ name: 'edit', params: { table: 'false' } });
                break;
            case CreationMenuTypeEnums.SIMPLE:
                // TODO: handle quick edit calls
                this.$router.push({ name: 'quickEdit', params: { quickEditFormId: item.formId || '' } });
                break;

            default:
                break;
        }
    }
    public onDownload(event: Event) {
        this.menuDownload = !this.menuDownload;
    }

    public onDownloadQuickGuide(event: Event) {
        this.menuQuickGuide = !this.menuQuickGuide;
    }

    public onFiltersUpdated(): void {
        FilterNewService.getAllFilters()
            .then((filters) => {
                this.filters = filters;
                this.filters.sort(this.sortFiltersInDashboard).reverse();
            })
            .catch((error) => {
                LoggerService.error(`Error on getAllFilters: `, error);
            });
    }

    public enableSettingsReports() {
        return this.isLoggedIn ? !(this.settingsReports.length > 0) : true;
    }

    public enableQuickGuide() {
        return this.isLoggedIn ? !(this.quickGuides && this.quickGuides.length > 0) : true;
    }

    public showUntisMenu() {
        this.showUnitsMenu = true;
    }

    public showDebugMenu() {
        if (this.debugEnabled === true) {
            this.debugMenu = true;
        }
    }

    public async getUserGreeting() {
        this.loadingOverlay = true;
        if (OnlineService.GetOnlineStatus === OnlineStatusEnums.ONLINE && AuthStore.GetIsLoggedIn === true) {
            await AuthentificationService.GetUser()
                .then((user: Oidc.User | null) => {
                    this.setUserGreeting(user);
                })
                .catch((error) => {
                    LoggerService.LogError(`Error on fetching user: `, error);
                })
                .finally(() => {
                    //this.loadingOverlay = false;
                });
        } else if (
            (OnlineService.GetOnlineStatus === OnlineStatusEnums.NO_NETWORK ||
                OnlineService.GetOnlineStatus === OnlineStatusEnums.API_ERROR ||
                OnlineService.GetOnlineStatus === OnlineStatusEnums.UNKNOWN) &&
            AuthStore.GetIsLoggedIn === true
        ) {
            this.setUserGreeting(null);
        }
        //this.loadingOverlay = false;
    }

    public onFilterDownload(inFilter: DexieFilter) {
        LoggerService.LogDebug(`onFilterDownload inFilter: `, inFilter);
        this.loadingOverlay = true;
        // just use a timeout as workaround tho show the spinner, before the export blocks the ui thread
        window.setTimeout(async () => {
            FilterNewService.UsedFilterForDataSets = inFilter;
            DataSetService.FetchFilteredDataSets().then(async () => {
                await ExcelExportService.generateExcelFromDataSets().finally(() => {
                    this.loadingOverlay = false;
                    FilterNewService.UsedFilterForDataSets = undefined;
                });
            });
        }, 500);
    }

    public isButtonDisabled(): boolean {
        let disabled = true;
        if (this.isLoggedIn === true && this.isWhitelisted === UserWhitelistEnums.WHITE && this.settingsReports.length > 0 && this.userGreeting) {
            disabled = false;
            this.loadingOverlay = false;
        }
        return disabled;
    }

    private setUserGreeting(inUser: Oidc.User | null) {
        if (inUser != null) {
            const userName: string = inUser.profile.name || this.$vuetify.lang.t('$vuetify.dashboard.noname');
            this.userGreeting = this.$vuetify.lang.t('$vuetify.dashboard.greeting', userName);
        } else {
            if (UserProfileStore.GetUserProfile && UserProfileStore.GetUserProfile.userName !== undefined) {
                this.userGreeting = this.$vuetify.lang.t('$vuetify.dashboard.greeting', UserProfileStore.GetUserProfile.userName);
            } else {
                this.userGreeting = this.$vuetify.lang.t('$vuetify.dashboard.login');
            }
        }
    }

    private async checkSession() {
        if (OnlineService.GetOnlineStatus === OnlineStatusEnums.ONLINE) {
            this.offlineMode = false;
            if (this.isLoggedIn === true) {
                await AuthentificationService.GetUser()
                    .then(async (user) => {
                        if (user != null && user.profile.sub) {
                            this.checkAppStartup(user.profile.sub);
                        }
                        await this.getUserGreeting();
                        this.tryDirectOpenDataSet();
                    })
                    .catch((error) => {
                        LoggerService.LogError('user error: ', error);
                    });
            } else {
                AuthentificationService.checkSessionStatus();
            }
        } else if (
            OnlineService.GetOnlineStatus === OnlineStatusEnums.NO_NETWORK ||
            OnlineService.GetOnlineStatus === OnlineStatusEnums.API_ERROR ||
            OnlineService.GetOnlineStatus === OnlineStatusEnums.UNKNOWN
        ) {
            if (AppSettingsService.GetAppSettings.lastLoggedInUserId !== undefined) {
                this.offlineMode = true;
                AuthStore.SetIsLoggedIn(true);
                await this.checkAppStartup(AppSettingsService.GetAppSettings.lastLoggedInUserId);
                await this.getUserGreeting();
                this.tryDirectOpenDataSet();
                
            } else {
                LoggerService.LogError('App Offline and no last logged in user saved');
            }
        } else {
            LoggerService.LogWarn(`unknown network status or user not logged in: `, OnlineStatusEnums[OnlineService.GetOnlineStatus], AuthStore.GetIsLoggedInText);
        }
    }

    private async checkAppStartup(inUserId: string) {
        await UserProfileStore.FetchUserProfile(inUserId).catch((error: any) => {
            LoggerService.LogError('user error: ', error);
        });

        if (this.appSettings !== undefined && this.getAppStarted === false) {
            await this.checkUserStatus();
        }
    }

    private async loadConfigData() {
        AppSettingsStore.SetAppStarted();
        this.loadingOverlay = true;
        const self = this;
        // loading config data and sync datasyet's
        await SynchronizationService.LoadConfigData()
            .then(async () => {
                // if auto sync is disbaled do not sync sync
                if (self.debugEnabled && self.appSettings && self.appSettings.autoSync === false) {
                    return;
                } else {
                    await SynchronizationService.SyncNow();
                    await SynchronizationService.SyncFiltersNow();
                    SynchronizationService.StartSyncService();
                }
            })
            .catch((error) => {
                LoggerService.LogError(`dashbaord SynchronizationService.LoadConfigData: `, error);
            })
            .finally(() => {
                //this.loadingOverlay = false;
            });

        if (this.offlineMode === false) {
            await this.checkSession();
        }
        this.getUserGreeting();
    }

    private sortFiltersInDashboard(lhs: DexieFilter, rhs: DexieFilter) {
        let ret = 0;
        if (lhs.createdAt !== undefined && rhs.createdAt !== undefined) {
            ret = Helpers.compareDateStrings(lhs.createdAt.toString(), rhs.createdAt.toString());
        }
        return ret;
    }

    private async checkUserStatus() {
        if (this.getAppStarted === false && this.isLoggedIn === true && this.isWhitelisted !== UserWhitelistEnums.WHITE) {
            ConfigurationService.OnUserWhitelistChanged.on((data) => {
                ConfigurationService.OnUserWhitelistChanged.off(() => null);
                const user = UserProfileStore.GetUserProfile;
                AuthStore.SetIsWhitelisted(AuthentificationService.CheckUserWhitelist(user.userEmail || ''));
                this.loadingOverlay = false;
            });
            await ConfigurationService.FetchUserWhitelist();
        } else if (this.getAppStarted === false && this.isLoggedIn === true && this.isWhitelisted === UserWhitelistEnums.WHITE) {
            await this.loadConfigData();
        } else if (this.getAppStarted === false && this.isLoggedIn === true && this.isWhitelisted !== UserWhitelistEnums.WHITE) {
            AppSettingsStore.SetAppStarted();
        }
        // else if (this.offlineMode === true
        //     && this.getAppStarted === false
        //     && this.isLoggedIn === true) {
        //     AuthStore.SetIsWhitelisted(UserWhitelistEnums.WHITE);
        //     await this.loadConfigData();
        // }

        if (this.offlineMode === false && this.getAppStarted === true) {
            await this.checkSession();
        }
        // this.getUserGreeting();
    }

    private setCreationMenuItems() {
        this.creationMenuItems = [];
        let itemId = 0;

        // add quick view wizard item first
        this.creationMenuItems.push({
            id: itemId++,
            type: CreationMenuTypeEnums.WIZARD,
            name: this.$vuetify.lang.t('$vuetify.dashboard.creation.wizard'),
        });

        // add defaault view item
        this.creationMenuItems.push({
            id: itemId++,
            type: CreationMenuTypeEnums.STANDARD,
            name: this.$vuetify.lang.t('$vuetify.dashboard.creation.standard'),
        });

        // add defaault view item
        this.creationMenuItems.push({
            id: itemId++,
            type: CreationMenuTypeEnums.MYMILL_WIZARD,
            name: this.$vuetify.lang.t('$vuetify.dashboard.creation.mymill_wizard'),
        });

        const items = QuickEditService.GetQuickEditItems;
        // TODO: add simplified menu items here
        // const x: any = [];
        items.forEach((item: QuickEditBaseModel) => {
            this.creationMenuItems.push({
                id: itemId++,
                type: CreationMenuTypeEnums.SIMPLE,
                name: this.getSimpleItemTitle(item),
                formId: item.formId,
            });
        });

        // add mobile view item last
        this.creationMenuItems.push({
            id: itemId++,
            type: CreationMenuTypeEnums.MOBILE,
            name: this.$vuetify.lang.t('$vuetify.dashboard.creation.mobile'),
        });
    }

    private getSimpleItemTitle(item: QuickEditBaseModel) {
        let ret: string = '';
        ret = this.$vuetify.lang.t(`$vuetify.identifiers.${item.formId}`);
        // fallback to name string if no translation found
        if (ret === undefined || ret.length === 0 || ret === item.formId || ret.includes('$vuetify.identifiers.')) {
            ret = item.name;
            LoggerService.LogDebug(`getIdentifierText fallback text for: ${item.formId} --> ${ret}`);
        }
        return ret;
    }

    private tryDirectOpenDataSet(): void {
        /**
         * first check if direct open datasedId is saved in appsettings
         * should be the case if app was not already open in the tab
         * and/or user needs to login first
         */
        let directDataSetId = (this.appSettings && this.appSettings.directOpenId) || undefined;
        if (directDataSetId == null) {
            /**
             * for the case it was not in appsettings, check also the
             * appsettings store
             */
            directDataSetId = AppSettingsStore.GetDirectOpenId;
        }
        if (directDataSetId != null) {
            const dataSet = DataSetService.GetAllDataSets().find((ds) => ds.dataSetId === directDataSetId);
            if (dataSet != null) {
                // const directFilter: DexieFilter = new DexieFilter();
                // directFilter.filterName = `Open ${directDataSetId}`;
                // directFilter.data.push(
                //     new DexieFilterField({
                //         identifier: ApiIdentifers.filterTabId,
                //         identifierPath: [ApiIdentifers.filterSortByDataSet, ApiIdentifers.filterTabId],
                //         name: this.$vuetify.lang.t(`$vuetify.identifiers.${ApiIdentifers.filterTabId}`),
                //         type: FieldTypeEnum.TEXT,
                //         valueString: directDataSetId
                //     })
                // );

                const dataSetDexieId: number = dataSet.id || -1;
                if (dataSetDexieId >= 0) {
                    this.$nextTick(() => {
                        this.$router.push({ name: 'preview', params: { directId: dataSetDexieId.toString() } });
                    });
                }
            }
        }
    }
}
